import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { officeGetCustomer, officeUpdateCustomer, officeResendCustomerInvite, officeResetCustomerActivation } from "@/lib/office.functions";
import { ArrowLeft, CheckCircle2, AlertTriangle, MailPlus, Save, ShieldAlert, Globe, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_employee/medewerker/office/klanten/$id")({
  head: () => ({ meta: [{ title: "Klant — EVG Office" }, { name: "robots", content: "noindex" }] }),
  component: CustomerDetail,
});

function fmt(dt?: string | null) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
}

function formatCountdown(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function CustomerDetail() {
  const { id } = Route.useParams();
  const getFn = useServerFn(officeGetCustomer);
  const updateFn = useServerFn(officeUpdateCustomer);
  const resendFn = useServerFn(officeResendCustomerInvite);
  const resetFn = useServerFn(officeResetCustomerActivation);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["office-customer", id],
    queryFn: () => getFn({ data: { id } }),
    refetchInterval: 30_000,
  });

  const [form, setForm] = useState<any>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (data?.profile) {
      setForm({
        full_name: data.profile.full_name ?? "",
        phone: data.profile.phone ?? "",
        customer_number: data.profile.customer_number ?? "",
        customer_type: data.profile.customer_type ?? "consumer",
        verification_status: data.profile.verification_status ?? "approved",
      });
    }
  }, [data?.profile]);

  useEffect(() => {
    if (typeof data?.cooldown_remaining_seconds === "number") {
      setCooldown(data.cooldown_remaining_seconds);
    }
  }, [data?.cooldown_remaining_seconds]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown > 0]);

  const saveMut = useMutation({
    mutationFn: () => updateFn({ data: { id, ...form } }),
    onSuccess: () => {
      toast.success("Klantgegevens opgeslagen");
      qc.invalidateQueries({ queryKey: ["office-customer", id] });
      qc.invalidateQueries({ queryKey: ["office-requests"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Opslaan mislukt"),
  });

  const resendMut = useMutation({
    mutationFn: () => resendFn({ data: { customer_id: id } }),
    onSuccess: (res: any) => {
      setConfirmOpen(false);
      if (res?.alreadyActivated) {
        toast.info("Klant heeft account al geactiveerd");
      } else if (res?.cooldown) {
        setCooldown(res.cooldown_remaining_seconds ?? 0);
        toast.warning(res.message ?? "Wachttijd actief");
      } else {
        toast.success("Uitnodigingsmail opnieuw verstuurd");
        setCooldown(res?.cooldown_seconds ?? 600);
      }
      qc.invalidateQueries({ queryKey: ["office-customer", id] });
    },
    onError: (e: any) => {
      setConfirmOpen(false);
      toast.error(e?.message ?? "Kon uitnodiging niet versturen");
      qc.invalidateQueries({ queryKey: ["office-customer", id] });
    },
  });

  const resetMut = useMutation({
    mutationFn: (reason: string) => resetFn({ data: { customer_id: id, reason: reason || undefined } }),
    onSuccess: (res: any) => {
      setResetOpen(false);
      setResetReason("");
      toast.success(
        res?.relinked_documents
          ? `Activatie gereset · ${res.relinked_documents} document(en) herkoppeld`
          : "Activatie gereset — je kunt nu opnieuw een uitnodiging versturen",
      );
      qc.invalidateQueries({ queryKey: ["office-customer", id] });
      qc.invalidateQueries({ queryKey: ["office-requests"] });
    },
    onError: (e: any) => {
      setResetOpen(false);
      toast.error(e?.message ?? "Reset mislukt");
    },
  });

  const resendHistory = useMemo(() => {
    const rows = (data?.audit_log ?? []).filter((r: any) => r.event_type === "invite_resend");
    return rows;
  }, [data?.audit_log]);

  if (isLoading || !data) return <div className="p-8 text-slate-400">Laden…</div>;

  const disabled = data.activated || cooldown > 0 || resendMut.isPending;

  return (
    <div className="space-y-5">
      <Link to="/medewerker/office/aanvragen" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0b2a4a]">
        <ArrowLeft className="size-3" /> Terug
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{data.profile.full_name ?? data.email ?? "Klant"}</h1>
          <p className="text-sm text-slate-500">
            <span className="font-mono">{data.profile.customer_number ?? "—"}</span> · {data.email ?? "—"} ·{" "}
            {data.activated ? (
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="size-3" /> Actief</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="size-3" /> Niet geactiveerd</span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center gap-2">
            {data.activated && (
              <button
                onClick={() => setResetOpen(true)}
                disabled={resetMut.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100 disabled:opacity-50"
                title="Activatie resetten zodat klant opnieuw kan activeren"
              >
                <RotateCcw className="size-4" /> Reset activatie
              </button>
            )}
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              title={
                data.activated
                  ? "Klant heeft al geactiveerd — reset eerst de activatie"
                  : cooldown > 0
                    ? `Beschikbaar over ${formatCountdown(cooldown)}`
                    : "Uitnodigingsmail opnieuw versturen"
              }
            >
              <MailPlus className="size-4" /> Uitnodiging opnieuw
            </button>
          </div>
          {cooldown > 0 && !data.activated && (
            <span className="text-[11px] text-slate-500">
              Volgende poging over <span className="font-mono">{formatCountdown(cooldown)}</span>
            </span>
          )}
          {data.activated && (
            <span className="text-[11px] text-slate-500">
              Alleen niet-geactiveerde accounts kunnen (opnieuw) worden geactiveerd.
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <h2 className="mb-4 text-sm font-semibold uppercase text-slate-500">Klantgegevens</h2>
          <div className="space-y-3 text-sm">
            <Field label="Naam"><input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1.5" /></Field>
            <Field label="Telefoon">
              <div className="flex items-center gap-2">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1.5" />
                {form.phone && (
                  <a
                    href={`/admin/whatsapp?phone=${encodeURIComponent(form.phone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100"
                    title="Open WhatsApp-chat met deze klant"
                  >
                    💬 Chat
                  </a>
                )}
              </div>
            </Field>
            <Field label="Klantnummer"><input value={form.customer_number} onChange={(e) => setForm({ ...form, customer_number: e.target.value.toUpperCase() })} placeholder="EVG-12345AB" className="w-full rounded border border-slate-200 px-2 py-1.5 font-mono" /></Field>
            <Field label="Type">
              <select value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1.5">
                <option value="consumer">Consument</option>
                <option value="business">Zakelijk</option>
              </select>
            </Field>
            <Field label="Verificatie">
              <select value={form.verification_status} onChange={(e) => setForm({ ...form, verification_status: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1.5">
                <option value="pending">In afwachting</option>
                <option value="approved">Goedgekeurd</option>
                <option value="rejected">Afgewezen</option>
              </select>
            </Field>
            <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="inline-flex items-center gap-1 rounded-lg bg-[#0b2a4a] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0a2340] disabled:opacity-50">
              <Save className="size-4" /> Opslaan
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase text-slate-500">Resend-geschiedenis</h2>
              <span className="text-[10px] text-slate-400">Laatste 50</span>
            </div>
            {resendHistory.length === 0 ? (
              <p className="text-xs text-slate-400">Nog geen resend-pogingen.</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-xs">
                {resendHistory.map((l: any) => (
                  <li key={l.id} className="py-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-slate-700">{fmt(l.created_at)}</div>
                        {l.error_message && <div className="mt-0.5 text-rose-600">Fout: {l.error_message}</div>}
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${l.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {l.status === "success" ? "Succes" : "Fout"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 text-[11px] text-slate-500">
              Laatste login: {fmt(data.last_sign_in_at)}
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <div className="mb-3 flex items-center gap-2">
              <ShieldAlert className="size-4 text-slate-500" />
              <h2 className="text-sm font-semibold uppercase text-slate-500">Auditlog (intern)</h2>
            </div>
            <p className="mb-2 text-[11px] text-slate-400">
              Bewaard voor onderbouwing bij misbruik of geschillen. Deze gegevens worden niet met de klant gedeeld.
            </p>
            {(data.audit_log ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">Nog geen gebeurtenissen.</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-xs">
                {(data.audit_log ?? []).slice(0, 20).map((l: any) => (
                  <li key={l.id} className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-700">{l.event_type}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${l.status === "success" ? "bg-emerald-100 text-emerald-700" : l.status === "error" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                        {l.status ?? "—"}
                      </span>
                      <span className="ml-auto text-slate-500">{fmt(l.created_at)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      {l.ip_address && (
                        <span className="inline-flex items-center gap-1"><Globe className="size-3" /> {l.ip_address}</span>
                      )}
                      {(l.city || l.country) && (
                        <span>{[l.city, l.region, l.country].filter(Boolean).join(", ")}</span>
                      )}
                      {l.user_agent && (
                        <span className="max-w-full truncate" title={l.user_agent}>UA: {l.user_agent.slice(0, 60)}{l.user_agent.length > 60 ? "…" : ""}</span>
                      )}
                    </div>
                    {l.error_message && <div className="mt-0.5 text-rose-600 text-[11px]">Fout: {l.error_message}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <h2 className="mb-3 text-sm font-semibold uppercase text-slate-500">Aanvragen ({data.requests.length})</h2>
            {data.requests.length === 0 ? (
              <p className="text-xs text-slate-400">Geen aanvragen.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {data.requests.map((r: any) => (
                  <li key={r.id}>
                    <Link to="/medewerker/office/aanvragen/$id" params={{ id: r.id }} className="flex justify-between hover:text-[#0b2a4a]">
                      <span>{r.service} — {r.stage}</span>
                      <span className="text-slate-400">{fmt(r.created_at)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setConfirmOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-5 text-amber-600" />
                <h3 className="text-base font-semibold text-slate-900">Uitnodiging opnieuw versturen?</h3>
              </div>
              <button onClick={() => setConfirmOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="size-4" />
              </button>
            </div>
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              <p>
                Je verstuurt een <strong>nieuwe wachtwoord-link</strong> naar <span className="font-mono">{data.email}</span>.
              </p>
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-800 ring-1 ring-amber-200">
                ⚠ De vorige uitnodigingslink wordt hierdoor ongeldig. De klant kan alleen nog de nieuwste link gebruiken.
              </p>
              <p className="text-[12px] text-slate-500">
                Na verzending geldt een wachttijd van {Math.round((data.cooldown_total_seconds ?? 600) / 60)} minuten voor de volgende poging.
              </p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Annuleren
              </button>
              <button
                onClick={() => resendMut.mutate()}
                disabled={resendMut.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                <MailPlus className="size-4" />
                {resendMut.isPending ? "Versturen…" : "Ja, verstuur opnieuw"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setResetOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <RotateCcw className="size-5 text-rose-600" />
                <h3 className="text-base font-semibold text-slate-900">Activatie resetten?</h3>
              </div>
              <button onClick={() => setResetOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="size-4" />
              </button>
            </div>
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              <p>
                Markeer het account van <span className="font-mono">{data.email}</span> als <strong>niet geactiveerd</strong>. Daarna kun je opnieuw een uitnodigingsmail versturen.
              </p>
              <ul className="list-disc space-y-1 pl-5 text-[13px] text-slate-600">
                <li>Bestaande sessies en de vorige uitnodigingslink vervallen zodra de klant opnieuw inlogt.</li>
                <li>Klantgegevens en <strong>documenten blijven bewaard</strong> en worden — voor de zekerheid — opnieuw op basis van e-mailadres aan dit account gekoppeld.</li>
                <li>Deze actie wordt vastgelegd in het interne auditlog (IP, locatie, medewerker).</li>
              </ul>
              <label className="block pt-1">
                <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Reden (optioneel)</span>
                <textarea
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Bijv. klant kan niet inloggen, wachtwoord vergeten na wisseling van e-mail"
                  className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setResetOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Annuleren
              </button>
              <button
                onClick={() => resetMut.mutate(resetReason)}
                disabled={resetMut.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                <RotateCcw className="size-4" />
                {resetMut.isPending ? "Resetten…" : "Ja, reset activatie"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{label}</span>
      {children}
    </label>
  );
}
